import { useCallback, useEffect, useRef, useState, useSyncExternalStore } from 'react';
import {
  computeShadowingBaseline,
  resolveSubYKey,
  snapSubY,
  type SubYStorageKey,
} from '@core/layout.ts';
import { clampCenterOffsetPx } from '@core/dubbing/layout.ts';
import {
  getShadowingCenterOffsetPx,
  setShadowingCenterOffsetPx,
} from '@core/dubbing/shadowingSession.ts';
import { DEFAULT_SETTINGS, type Settings } from '@core/settings/schema.ts';
import type { SettingsStore } from '@ports/SettingsStore.ts';
import type { VideoController } from '@ports/VideoController.ts';
import type { DeviceDim, DeviceOrientation } from './useDeviceClass.ts';
import { findCueSeekTarget, type CueSeekKind } from '../lib/cueSeek.ts';
import { SHORTCUT_DELAY_STEP_MS, SHORTCUT_FONT_MAX, SHORTCUT_FONT_MIN } from '../lib/shortcutSteps.ts';
import type { SubtitleStore } from '../state/subtitleStore.ts';

export const STAGE_RAILS_AUTOHIDE_MS = 3500;
export const STAGE_RAILS_SLIDER_AUTOHIDE_MS = 5000;

export type StageRailsEligibility = {
  ready: boolean;
  dim: DeviceDim;
  practiceMode: string;
  composeOpen: boolean;
  panelOpen: boolean;
  settingsOpen: boolean;
  loginOpen: boolean;
};

/** Watch, learning, or shadowing — never dictation/speaking (compose / pill layouts). */
export function isStageRailsEligible(opts: StageRailsEligibility): boolean {
  if (!opts.ready) return false;
  if (
    opts.practiceMode !== 'watch'
    && opts.practiceMode !== 'learning'
    && opts.practiceMode !== 'shadowing'
  ) {
    return false;
  }
  if (opts.composeOpen || opts.panelOpen || opts.settingsOpen || opts.loginOpen) {
    return false;
  }
  // D0 included so desktop / wide windows still reveal rails while developing.
  void opts.dim;
  return true;
}

export type StageRailsLayout = 'sides' | 'ends';
export type StageRailsToolsMode =
  | 'idle'
  | 'position'
  | 'fontSize'
  | 'center'
  | 'delayMain'
  | 'delaySecond';

export function stageRailsLayoutFor(orientation: DeviceOrientation): StageRailsLayout {
  return orientation === 'portrait' ? 'ends' : 'sides';
}

/** Map session center offset (px) ↔ rail slider 0–1 (0.5 = natural center). */
export function centerOffsetNormFromPx(offsetPx: number, viewWidth: number): number {
  if (!(viewWidth > 0)) return 0.5;
  return Math.min(1, Math.max(0, offsetPx / viewWidth + 0.5));
}

export function centerOffsetPxFromNorm(norm: number, viewWidth: number): number {
  const n = Number.isFinite(norm) ? Math.min(1, Math.max(0, norm)) : 0.5;
  return clampCenterOffsetPx((n - 0.5) * viewWidth, viewWidth);
}

function clampFontSize(n: number): number {
  return Math.min(
    SHORTCUT_FONT_MAX,
    Math.max(SHORTCUT_FONT_MIN, Math.round(n * 100) / 100),
  );
}

function readSettings(settingsStore: SettingsStore): Settings {
  return settingsStore.getSnapshot?.() ?? DEFAULT_SETTINGS;
}

const STAGE_TAP_IGNORE =
  '.stage-rails, .stage-rails-reveal, .player__embed, iframe, input, textarea, select, button, a, .settings-panel, .track-selector, .cue-panel, .login-dialog, .learning-popup, .dictation-compose, .dictation-tap-pill, .player__scrim, .player__chrome, .player__bilingual-chip, .subtitle-overlay, .unisubs-learning, .unisubs-token, .unisubs-token-gap, .textBasedSub, #mainSub, #secondSub';

export type UseStageRailsOptions = {
  video: VideoController | null;
  store: SubtitleStore;
  settingsStore: SettingsStore;
  ready: boolean;
  dim: DeviceDim;
  orientation: DeviceOrientation;
  practiceMode: string;
  composeOpen: boolean;
  panelOpen: boolean;
  settingsOpen: boolean;
  loginOpen: boolean;
  /** Re-paint overlay after session-only shadowing center nudge. */
  onInvalidate?: () => void;
};

export type UseStageRailsResult = {
  eligible: boolean;
  visible: boolean;
  toolsMode: StageRailsToolsMode;
  pinned: boolean;
  layout: StageRailsLayout;
  /** True when the video is paused (or not ready). */
  paused: boolean;
  subY: number;
  fontSize: number;
  /** Shadowing center-line slider 0–1 (0.5 = natural). */
  centerOffset: number;
  /** Show ↔ center tool (shadowing only). */
  showCenterControl: boolean;
  /** Main / second track delay in ms (subtitle sync). */
  mainDelayMs: number;
  secondDelayMs: number;
  /** Call from stage pointerdown after filtering iframe / rails / form fields. */
  onStagePointerDown: (target: EventTarget | null) => void;
  /** Force-show rails (full-stage reveal tap while hidden). */
  showRails: () => void;
  /** Hide rails (center dismiss). No-op while pinned. */
  hideRails: () => void;
  /** Play / pause via VideoController. */
  togglePlayback: () => void;
  onInteract: () => void;
  seekPrev: () => void;
  seekRepeat: () => void;
  seekNext: () => void;
  /** Open / close per-track delay trim (±100ms, extension Q/E step). */
  toggleDelayMainMode: () => void;
  toggleDelaySecondMode: () => void;
  nudgeDelayMain: (dir: 1 | -1) => void;
  nudgeDelaySecond: (dir: 1 | -1) => void;
  togglePositionMode: () => void;
  toggleFontSizeMode: () => void;
  toggleCenterMode: () => void;
  togglePin: () => void;
  setSubY: (value: number) => void;
  setFontSize: (value: number) => void;
  setCenterOffset: (value: number) => void;
};

/**
 * Ephemeral stage rails for watch mode (phone/tablet; desktop allowed for testing).
 */
export function useStageRails({
  video,
  store,
  settingsStore,
  ready,
  dim,
  orientation,
  practiceMode,
  composeOpen,
  panelOpen,
  settingsOpen,
  loginOpen,
  onInvalidate,
}: UseStageRailsOptions): UseStageRailsResult {
  const [visible, setVisible] = useState(false);
  const [toolsMode, setToolsMode] = useState<StageRailsToolsMode>('idle');
  const [pinned, setPinned] = useState(false);
  const [paused, setPaused] = useState(true);
  const [centerOffset, setCenterOffsetState] = useState(0.5);
  const settings = useSyncExternalStore(
    (cb) => settingsStore.subscribe(cb),
    () => readSettings(settingsStore),
    () => DEFAULT_SETTINGS,
  );
  const main = useSyncExternalStore(
    (cb) => store.subscribe(cb),
    () => store.getSlot('main'),
  );
  const second = useSyncExternalStore(
    (cb) => store.subscribe(cb),
    () => store.getSlot('second'),
  );

  const hideTimer = useRef<number | null>(null);
  const toolsModeRef = useRef(toolsMode);
  toolsModeRef.current = toolsMode;
  const pinnedRef = useRef(pinned);
  pinnedRef.current = pinned;
  const invalidateRef = useRef(onInvalidate);
  invalidateRef.current = onInvalidate;

  const eligible = isStageRailsEligible({
    ready,
    dim,
    practiceMode,
    composeOpen,
    panelOpen,
    settingsOpen,
    loginOpen,
  });

  const showCenterControl = practiceMode === 'shadowing';
  const layout = stageRailsLayoutFor(orientation);

  const playbackViewWidth = useCallback((): number => {
    if (!video) return 0;
    if (practiceMode !== 'shadowing') {
      return video.getViewportRect().width;
    }
    const box = video.getPlaybackClientRect();
    // Mobile is YouTube-only — flush to playback box edges.
    return computeShadowingBaseline(
      { width: box.width, height: box.height },
      'mainSubtitle',
      {
        subtitleSeparation: Boolean(settings.subtitleSeparation),
        subY: Number(settings.subY ?? 0.8),
        mainSubY: Number(settings.mainSubY ?? 0.8),
        secondSubY: Number(settings.secondSubY ?? 0.8),
      },
      { flushToPlaybackBox: true },
    ).viewWidth;
  }, [video, practiceMode, settings.subtitleSeparation, settings.subY, settings.mainSubY, settings.secondSubY]);

  const syncCenterFromSession = useCallback(() => {
    if (practiceMode !== 'shadowing') {
      setCenterOffsetState(0.5);
      return;
    }
    const viewWidth = playbackViewWidth();
    const offsetPx = getShadowingCenterOffsetPx(
      'main',
      Boolean(settings.subtitleSeparation),
    );
    setCenterOffsetState(centerOffsetNormFromPx(offsetPx, viewWidth));
  }, [practiceMode, playbackViewWidth, settings.subtitleSeparation]);

  useEffect(() => {
    syncCenterFromSession();
    if (practiceMode !== 'shadowing' && toolsModeRef.current === 'center') {
      setToolsMode('idle');
    }
  }, [practiceMode, syncCenterFromSession]);

  const clearHideTimer = useCallback(() => {
    if (hideTimer.current != null) {
      window.clearTimeout(hideTimer.current);
      hideTimer.current = null;
    }
  }, []);

  const scheduleHide = useCallback(() => {
    clearHideTimer();
    if (pinnedRef.current) return;
    const ms = toolsModeRef.current !== 'idle'
      ? STAGE_RAILS_SLIDER_AUTOHIDE_MS
      : STAGE_RAILS_AUTOHIDE_MS;
    hideTimer.current = window.setTimeout(() => {
      if (pinnedRef.current) return;
      setVisible(false);
      setToolsMode('idle');
      hideTimer.current = null;
    }, ms);
  }, [clearHideTimer]);

  const onInteract = useCallback(() => {
    if (!eligible) return;
    setVisible(true);
    scheduleHide();
  }, [eligible, scheduleHide]);

  const showRails = useCallback(() => {
    if (
      !ready
      || (
        practiceMode !== 'watch'
        && practiceMode !== 'learning'
        && practiceMode !== 'shadowing'
      )
      || composeOpen
    ) {
      return;
    }
    setVisible(true);
    scheduleHide();
  }, [ready, practiceMode, composeOpen, scheduleHide]);

  const hideRails = useCallback(() => {
    if (pinnedRef.current) return;
    clearHideTimer();
    setVisible(false);
    setToolsMode('idle');
  }, [clearHideTimer]);

  const togglePlayback = useCallback(() => {
    if (!video) return;
    if (video.isPaused()) video.play();
    else video.pause();
  }, [video]);

  useEffect(() => {
    if (!video) {
      setPaused(true);
      return undefined;
    }
    const sync = () => setPaused(video.isPaused());
    sync();
    const offPlay = video.on('play', sync);
    const offPause = video.on('pause', sync);
    return () => {
      offPlay();
      offPause();
    };
  }, [video]);

  // First eligibility: brief pulse so rails are discoverable.
  const pulsedRef = useRef(false);
  useEffect(() => {
    if (!eligible) {
      pulsedRef.current = false;
      return;
    }
    if (pulsedRef.current) return;
    pulsedRef.current = true;
    setVisible(true);
    scheduleHide();
  }, [eligible, scheduleHide]);

  useEffect(() => {
    if (!eligible) {
      clearHideTimer();
      setVisible(false);
      setToolsMode('idle');
      setPinned(false);
    }
  }, [eligible, clearHideTimer]);

  useEffect(() => {
    if (eligible && visible) scheduleHide();
    else if (!visible) clearHideTimer();
  }, [visible, eligible, toolsMode, pinned, scheduleHide, clearHideTimer]);

  useEffect(() => () => clearHideTimer(), [clearHideTimer]);

  const seek = useCallback(
    (kind: CueSeekKind) => {
      if (!video) return;
      const mainSlot = store.getSlot('main');
      const secondSlot = store.getSlot('second');
      const cues = mainSlot.cues?.length ? mainSlot.cues : secondSlot.cues;
      if (!cues?.length) return;
      const delayMs = (mainSlot.cues?.length ? mainSlot : secondSlot).view.delayMs;
      const timeMs = video.getCurrentTimeMs() + delayMs;
      const target = findCueSeekTarget(cues, timeMs, kind);
      if (!target) return;
      video.seekMs(target.start);
      video.play();
    },
    [video, store],
  );

  const nudgeDelay = useCallback(
    (slot: 'main' | 'second', dir: 1 | -1) => {
      const cur = store.getSlot(slot).view.delayMs;
      store.setViewState(slot, {
        delayMs: Math.round(cur + dir * SHORTCUT_DELAY_STEP_MS),
      });
    },
    [store],
  );

  const writeSubY = useCallback(
    (value: number) => {
      const snap = snapSubY(value);
      const separation = Boolean(settings.subtitleSeparation);
      const keys: SubYStorageKey[] = separation
        ? ['mainSubY', 'secondSubY']
        : [resolveSubYKey('main', false)];
      const patch: Partial<Record<SubYStorageKey, number>> = {};
      for (const key of keys) patch[key] = snap;
      void settingsStore.set(patch);
    },
    [settings.subtitleSeparation, settingsStore],
  );

  const writeFontSize = useCallback(
    (value: number) => {
      const next = clampFontSize(value);
      void settingsStore.set({
        mainFontSizeRatio: next,
        secondFontSizeRatio: next,
      });
    },
    [settingsStore],
  );

  const writeCenterOffset = useCallback(
    (norm: number) => {
      const viewWidth = playbackViewWidth();
      const offsetPx = centerOffsetPxFromNorm(norm, viewWidth);
      const separation = Boolean(settings.subtitleSeparation);
      setCenterOffsetState(centerOffsetNormFromPx(offsetPx, viewWidth));
      if (separation) {
        setShadowingCenterOffsetPx('main', true, offsetPx);
        setShadowingCenterOffsetPx('second', true, offsetPx);
      } else {
        setShadowingCenterOffsetPx('main', false, offsetPx);
      }
      invalidateRef.current?.();
    },
    [playbackViewWidth, settings.subtitleSeparation],
  );

  const currentSubY = settings.subtitleSeparation
    ? settings.mainSubY
    : settings.subY;

  const onStagePointerDown = useCallback(
    (target: EventTarget | null) => {
      if (!eligible) return;
      const el = target as HTMLElement | null;
      if (!el?.closest) return;
      if (el.closest(STAGE_TAP_IGNORE)) return;
      if (pinnedRef.current) return;
      setVisible((v) => {
        if (v) {
          setToolsMode('idle');
          return false;
        }
        return true;
      });
    },
    [eligible],
  );

  return {
    eligible,
    visible: eligible && visible,
    toolsMode: eligible && visible ? toolsMode : 'idle',
    pinned: eligible && visible && pinned,
    layout,
    paused,
    subY: currentSubY,
    fontSize: settings.mainFontSizeRatio,
    centerOffset,
    showCenterControl,
    mainDelayMs: main.view.delayMs,
    secondDelayMs: second.view.delayMs,
    onStagePointerDown,
    showRails,
    hideRails,
    togglePlayback,
    onInteract,
    seekPrev: () => seek('prev'),
    seekRepeat: () => seek('repeat'),
    seekNext: () => seek('next'),
    toggleDelayMainMode: () => {
      setToolsMode((m) => (m === 'delayMain' ? 'idle' : 'delayMain'));
      setVisible(true);
    },
    toggleDelaySecondMode: () => {
      setToolsMode((m) => (m === 'delaySecond' ? 'idle' : 'delaySecond'));
      setVisible(true);
    },
    nudgeDelayMain: (dir) => nudgeDelay('main', dir),
    nudgeDelaySecond: (dir) => nudgeDelay('second', dir),
    togglePositionMode: () => {
      setToolsMode((m) => (m === 'position' ? 'idle' : 'position'));
      setVisible(true);
    },
    toggleFontSizeMode: () => {
      setToolsMode((m) => (m === 'fontSize' ? 'idle' : 'fontSize'));
      setVisible(true);
    },
    toggleCenterMode: () => {
      if (practiceMode !== 'shadowing') return;
      // Re-read session offset (handle drag may have moved it since last rails open).
      syncCenterFromSession();
      setToolsMode((m) => (m === 'center' ? 'idle' : 'center'));
      setVisible(true);
    },
    togglePin: () => {
      setPinned((p) => !p);
      setVisible(true);
    },
    setSubY: writeSubY,
    setFontSize: writeFontSize,
    setCenterOffset: writeCenterOffset,
  };
}
